/**
 * Lock Day confirmation (mandate §10, SDD §8.1) — composed surface, DEC-065 precedent.
 *
 * The SDD names the shape: manual lockout, OPTIONAL close-all, confirm required. The dialog says what cannot
 * be unsaid — there is no unlock control, the calendar ends it — because a lock a trader can casually undo is
 * not the instrument §8.1 describes.
 */
import type { ViewModel } from '@/screens/generated/view-model';
import { css } from '@pointfive/ui';

const overlay = css(
  'position:fixed;inset:0;z-index:70;display:flex;align-items:center;justify-content:center;background:color-mix(in srgb, var(--bg) 55%, transparent);backdrop-filter:blur(2px)',
);
const sheet = css(
  'width:380px;max-width:calc(100vw - 40px);border-radius:16px;border:1px solid var(--card-line);background:var(--surface);box-shadow:var(--shadow-float);padding:18px',
);
const heading = css('font-size:13px;font-weight:600;margin:0 0 6px');
const body = css('font-size:11.5px;color:var(--text-mid);line-height:1.6;margin:0 0 14px');
const row = css('display:flex;gap:8px;justify-content:flex-end');
const ghostBtn = css(
  'font-size:11.5px;padding:7px 14px;border-radius:99px;border:1px solid var(--l10);background:transparent;color:var(--text-mid);cursor:pointer',
);
const dangerBtn = css(
  'font-size:11.5px;font-weight:600;padding:7px 15px;border-radius:99px;border:1px solid color-mix(in srgb, var(--neg) 45%, transparent);background:color-mix(in srgb, var(--neg) 10%, transparent);color:var(--neg);cursor:pointer',
);
const checkRow = css(
  'display:flex;align-items:center;gap:9px;font-size:11.5px;color:var(--text-mid);margin:0 0 14px;cursor:pointer;user-select:none',
);

export function LockDay({ m }: { m: Partial<ViewModel> }) {
  const d = m.lockDayOpen;
  if (!d) return null;
  const openCount = m.lockDayOpenCount ?? 0;

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label="Lock the trading day">
      <div style={sheet}>
        <h2 style={heading}>Lock the day?</h2>
        <p style={body}>
          Order entry on every linked account refuses until the end of the UTC day. There is no unlock — the
          calendar ends it. Exits still work: closing or reducing a position is never blocked by a lockout.
        </p>
        <label style={checkRow}>
          <input
            type="checkbox"
            checked={d.closeAll}
            onChange={m.lockDayToggleCloseAll}
            disabled={d.busy}
            aria-label="Close all open positions first"
          />
          Close all open positions first
          {openCount > 0 ? (
            <span style={css('color:var(--muted);font-size:10.5px')}>({openCount} open)</span>
          ) : (
            <span style={css('color:var(--faint);font-size:10.5px')}>(none open)</span>
          )}
        </label>
        <div style={row}>
          <button style={ghostBtn} onClick={m.lockDayCancel} disabled={d.busy}>
            Keep trading
          </button>
          <button style={dangerBtn} onClick={m.lockDayGo} disabled={d.busy} aria-label="Confirm lock day">
            {d.busy ? 'Locking…' : 'Lock the day'}
          </button>
        </div>
      </div>
    </div>
  );
}
